// Phonetic + fuzzy word matching for speech-to-text orthography drift.
//
// STT output is phonetically right and orthographically wrong: "Ruth
// Winter" arrives as "Roof winter", "Twelfth" as "Twelveth". Exact
// string matching makes every such row fail; these helpers let the
// parser match by sound in the two places where that is safe — fee
// earner names and date words — while everything else stays strict.

/**
 * Reduce a word to a crude British-speech phonetic key.
 * th→f models th-fronting ("Ruth"→"Roof"); z→s, c/ck→k merge common
 * confusions; non-leading vowels and h are dropped; repeats collapse.
 */
export function phoneticKey(word: string): string {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!w) return '';
  let s = w
    .replace(/ph/g, 'f')
    .replace(/th/g, 'f')
    .replace(/ck/g, 'k')
    .replace(/c/g, 'k')
    .replace(/z/g, 's')
    .replace(/q/g, 'k');
  const head = s[0]!;
  let tail = s.slice(1).replace(/[aeiouyh]/g, '');
  s = head + tail;
  return s.replace(/(.)\1+/g, '$1');
}

/** Words match phonetically when their keys are equal (both non-empty). */
export function phoneticEquals(a: string, b: string): boolean {
  const ka = phoneticKey(a);
  const kb = phoneticKey(b);
  return ka.length > 0 && ka === kb;
}

/**
 * Damerau–Levenshtein distance (optimal string alignment): insertions,
 * deletions, substitutions and adjacent transpositions each cost 1.
 * Transpositions matter — "januray"→"january" is one slip, not two.
 */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
      }
    }
  }
  return d[m]![n]!;
}

/**
 * Find `word` in `vocabulary`, tolerating small misspellings.
 * Exact match wins; otherwise the closest word within an edit-distance
 * budget (1 for words of 5–7 letters, 2 for 8+; shorter words must be
 * exact — too many real words collide at that length).
 */
export function fuzzyLookup(word: string, vocabulary: readonly string[]): string | null {
  const w = word.toLowerCase();
  for (const v of vocabulary) if (v === w) return v;
  if (w.length < 5) return null;
  const budget = w.length >= 8 ? 2 : 1;
  let best: string | null = null;
  let bestDist = budget + 1;
  for (const v of vocabulary) {
    if (Math.abs(v.length - w.length) > budget) continue;
    const d = levenshtein(w, v);
    if (d < bestDist) {
      bestDist = d;
      best = v;
    }
  }
  return bestDist <= budget ? best : null;
}
